/**
 * AddMembersModal - Modal for adding members to an existing group chat
 *
 * Filters out users already in the chat and allows multi-select
 * Notion-style design matching other team chat components
 */

import React, { useState, useMemo } from 'react';
import { X, Check, Loader2, UserPlus } from 'lucide-react';
import { useTeamChatStore, type TeamChat } from '../../lib/stores/teamChatStore';

interface AddMembersModalProps {
  isOpen: boolean;
  chat: TeamChat;
  onClose: () => void;
}

export function AddMembersModal({ isOpen, chat, onClose }: AddMembersModalProps) {
  const { organizationUsers } = useTeamChatStore();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  if (!isOpen) return null;

  // Filter out users already in the chat
  const existingParticipantIds = useMemo(() => {
    return new Set((chat.participants || []).map(p => p.user_id));
  }, [chat.participants]);

  const availableUsers = useMemo(() => {
    return organizationUsers.filter(u => !existingParticipantIds.has(u.id));
  }, [organizationUsers, existingParticipantIds]);

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAdd = async () => {
    if (selectedUserIds.length === 0 || adding) return;

    setAdding(true);
    try {
      await useTeamChatStore.getState().addParticipants(chat.id, selectedUserIds);
      handleClose();
    } catch (error) {
      console.error('Error adding participants:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    onClose();
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="fixed inset-0 z-[66] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal - Notion style */}
      <div
        className="relative bg-white w-full max-w-md overflow-hidden"
        style={{
          borderRadius: '8px',
          border: '1px solid rgba(55, 53, 47, 0.09)',
          boxShadow: 'rgba(55, 53, 47, 0.1) 0px 4px 12px, rgba(55, 53, 47, 0.04) 0px 16px 32px'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 bg-white"
          style={{ borderBottom: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
            <h3 className="font-semibold" style={{ color: '#37352F' }}>Aggiungi membri</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Users list */}
        <div className="max-h-64 overflow-y-auto">
          {availableUsers.length === 0 ? (
            <div className="text-center py-8 px-4" style={{ color: '#787774' }}>
              Tutti gli utenti sono già nel gruppo
            </div>
          ) : (
            availableUsers.map(user => {
              const isSelected = selectedUserIds.includes(user.id);

              return (
                <button
                  key={user.id}
                  onClick={() => handleUserToggle(user.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left"
                  style={{
                    backgroundColor: isSelected ? 'rgba(35, 131, 226, 0.08)' : 'transparent',
                    borderBottom: '1px solid rgba(55, 53, 47, 0.04)'
                  }}
                  onMouseEnter={(e) => !isSelected && (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.04)')}
                  onMouseLeave={(e) => !isSelected && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {/* Avatar */}
                  {user.profile_image_url ? (
                    <img
                      src={user.profile_image_url}
                      alt={user.full_name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                      style={{ backgroundColor: 'rgba(55, 53, 47, 0.08)', color: '#37352F' }}
                    >
                      {getInitials(user.full_name)}
                    </div>
                  )}

                  {/* Name */}
                  <span className="flex-1 text-sm" style={{ color: '#37352F' }}>
                    {user.full_name}
                  </span>

                  {/* Checkbox - Notion style */}
                  <div
                    className="w-[18px] h-[18px] rounded flex items-center justify-center transition-colors flex-shrink-0"
                    style={{
                      backgroundColor: isSelected ? '#2383E2' : 'transparent',
                      border: isSelected ? 'none' : '2px solid rgba(55, 53, 47, 0.3)'
                    }}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={2.5} />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 py-3 flex items-center justify-between bg-white"
          style={{ borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <span className="text-sm" style={{ color: '#787774' }}>
            {selectedUserIds.length} selezionati
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 rounded-md transition-colors text-sm font-medium"
              style={{ color: '#37352F' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              Annulla
            </button>
            <button
              onClick={handleAdd}
              disabled={selectedUserIds.length === 0 || adding}
              className="px-3 py-1.5 rounded-md text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#2383E2' }}
              onMouseEnter={(e) => !adding && selectedUserIds.length > 0 && (e.currentTarget.style.backgroundColor = '#1a6cb8')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2383E2')}
            >
              {adding ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Aggiunta...
                </>
              ) : (
                'Aggiungi'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
